'use client'

import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Shield, ChevronRight, Home, Download, CheckCircle2, Lock, Eye, Database, UserCheck, Globe, RefreshCw } from 'lucide-react'
import Link from 'next/link'

export default function PrivacyPolicyPage() {
  const [activeSection, setActiveSection] = useState<string>('')

  const sections = [
    { id: 'introduction', title: '1. Introduction', icon: Shield },
    { id: 'collection', title: '2. Information We Collect', icon: Database },
    { id: 'usage', title: '3. How We Use Your Information', icon: UserCheck },
    { id: 'sharing', title: '4. How We Share Your Information', icon: Globe },
    { id: 'security', title: '5. Data Security', icon: Lock },
    { id: 'rights', title: '6. Your Privacy Rights', icon: Eye },
    { id: 'updates', title: '7. Policy Updates', icon: RefreshCw },
  ]

  const scrollToSection = (id: string) => {
    setActiveSection(id)
    const element = document.getElementById(id)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 text-white py-16 px-4 sm:px-6 lg:px-8 shadow-2xl">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center shadow-lg">
              <Shield className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-5xl font-bold mb-2">Privacy Policy</h1>
              <p className="text-indigo-100 text-lg">How We Protect and Use Your Information</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-8">
            <Badge className="bg-white/20 text-white border-white/30 px-4 py-2 text-sm backdrop-blur-sm">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Last Updated: January 7, 2026
            </Badge>
            <Badge className="bg-green-500/20 text-green-100 border-green-400/30 px-4 py-2 text-sm backdrop-blur-sm">
              <Lock className="w-4 h-4 mr-2" />
              HIPAA Compliant
            </Badge>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link href="/">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm">
                <Home className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
            <Link href="/terms">
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm">
                View Terms of Service
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-6">
              <Card className="shadow-lg border-0">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="w-5 h-5 text-indigo-600" />
                    Quick Navigation
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <nav className="space-y-1">
                    {sections.map((section) => {
                      const Icon = section.icon
                      return (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all flex items-center gap-2 ${
                            activeSection === section.id
                              ? 'bg-gradient-to-r from-indigo-100 to-purple-100 text-indigo-700 font-semibold shadow-sm'
                              : 'text-slate-600 hover:bg-slate-100'
                          }`}
                        >
                          <Icon className="w-4 h-4" />
                          <span className="flex-1">{section.title}</span>
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      )
                    })}
                  </nav>
                </CardContent>
              </Card>

              <Card className="shadow-lg border-0 bg-gradient-to-br from-green-50 to-emerald-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                      <Lock className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-bold text-slate-900">Your Data is Safe</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    We use industry-standard encryption and security measures to protect your information
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">SSL Encrypted</Badge>
                    <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">PCI Compliant</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Card className="shadow-xl border-0">
              <CardContent className="p-8 lg:p-12">
                {/* Introduction */}
                <section id="introduction" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8 shadow-sm">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-blue-900 mb-2">Your Privacy Matters</h3>
                        <p className="text-blue-800 leading-relaxed">
                          American Clinical Experience is committed to protecting your privacy and ensuring the security of your personal information. 
                          This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform.
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 2 - Information We Collect */}
                <section id="collection" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <Database className="w-6 h-6 text-indigo-700" />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">Information We Collect</h2>
                  </div>
                  <div className="space-y-6">
                    <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                      <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <UserCheck className="w-5 h-5 text-indigo-600" />
                        Personal Information
                      </h3>
                      <p className="text-slate-700 leading-relaxed mb-4">
                        We collect personal information that you voluntarily provide when you register, book placements, or contact us:
                      </p>
                      
                      {/* Signup Data */}
                      <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200 mb-4">
                        <h4 className="font-semibold text-indigo-900 mb-2 text-sm">📝 Information Collected at Account Signup</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <div className="flex items-center gap-2 text-sm text-indigo-800">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span>Email address (required, verified)</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-indigo-800">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span>First and last name</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-indigo-800">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span>Phone number (optional)</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-indigo-800">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span>School / University name</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-indigo-800">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span>Program of study (e.g., FNP, PMHNP)</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-indigo-800">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span>Zip code / Location</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-indigo-800">
                            <CheckCircle2 className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                            <span>Account password (securely hashed)</span>
                          </div>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Payment and billing information</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Professional credentials</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Emergency contact information</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Required health documentation</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Clinical experience history</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span>Booking and rotation preferences</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                      <h3 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                        <Eye className="w-5 h-5 text-indigo-600" />
                        Automatically Collected Information
                      </h3>
                      <p className="text-slate-700 leading-relaxed mb-4">
                        We automatically collect certain information when you use our platform:
                      </p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <span>IP address, browser type, and device information</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <span>Pages viewed and time spent on pages</span>
                        </li>
                        <li className="flex items-start gap-2 text-sm text-slate-700">
                          <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full mt-1.5 flex-shrink-0"></span>
                          <span>Cookies and similar tracking technologies</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 3 - How We Use Information */}
                <section id="usage" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <UserCheck className="w-6 h-6 text-indigo-700" />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">How We Use Your Information</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-lg border border-blue-200">
                      <h4 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs">🎯</span>
                        Service Delivery
                      </h4>
                      <p className="text-blue-800 text-sm">Process bookings, manage placements, and facilitate communications</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-lg border border-green-200">
                      <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">💳</span>
                        Payment Processing
                      </h4>
                      <p className="text-green-800 text-sm">Handle deposits, installments, and refunds securely</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-5 rounded-lg border border-purple-200">
                      <h4 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs">📧</span>
                        Communication
                      </h4>
                      <p className="text-purple-800 text-sm">Send confirmations, receipts, updates, and important notices</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-5 rounded-lg border border-amber-200">
                      <h4 className="font-bold text-amber-900 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center text-white text-xs">🔒</span>
                        Security
                      </h4>
                      <p className="text-amber-800 text-sm">Prevent fraud, detect issues, and protect user safety</p>
                    </div>
                  </div>
                </section>

                {/* Section 4 - Sharing Information */}
                <section id="sharing" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-l-4 border-purple-500 p-8 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center shadow-sm">
                        <Globe className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">How We Share Your Information</h2>
                    </div>
                    
                    {/* Important Privacy Commitments */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <h4 className="font-bold text-green-900">We Do NOT Sell Your Data</h4>
                        </div>
                        <p className="text-green-800 text-sm">We do not sell or rent your personal information to third parties for marketing purposes.</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300">
                        <div className="flex items-center gap-2 mb-2">
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                          <h4 className="font-bold text-green-900">SMS Data Protection</h4>
                        </div>
                        <p className="text-green-800 text-sm">We do not share text messaging opt-in data with third parties for marketing purposes.</p>
                      </div>
                    </div>

                    <p className="text-slate-700 leading-relaxed mb-6">
                      We may share your information only in the following limited circumstances:
                    </p>
                    <div className="space-y-4">
                      <div className="bg-white p-5 rounded-lg border border-purple-200">
                        <h4 className="font-bold text-purple-900 mb-2">✅ With Your Consent</h4>
                        <p className="text-slate-700 text-sm">When you explicitly consent, such as applying for a placement with a preceptor</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border border-purple-200">
                        <h4 className="font-bold text-purple-900 mb-2">🔧 With Service Providers</h4>
                        <p className="text-slate-700 text-sm">Payment processors (Stripe), authentication services (Clerk), and hosting providers</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border border-purple-200">
                        <h4 className="font-bold text-purple-900 mb-2">🏥 With Preceptors & Clinical Sites</h4>
                        <p className="text-slate-700 text-sm">Necessary student information to facilitate clinical placements</p>
                      </div>
                      <div className="bg-white p-5 rounded-lg border border-purple-200">
                        <h4 className="font-bold text-purple-900 mb-2">⚖️ For Legal Reasons</h4>
                        <p className="text-slate-700 text-sm">When required by law or to protect rights and safety</p>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 5 - Security */}
                <section id="security" className="mb-12 scroll-mt-6">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-8 rounded-r-xl shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center shadow-sm">
                        <Lock className="w-6 h-6 text-white" />
                      </div>
                      <h2 className="text-3xl font-bold text-slate-900">Data Security</h2>
                    </div>
                    <p className="text-slate-700 leading-relaxed mb-6">
                      We implement comprehensive security measures to protect your information:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-lg border border-green-200 flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Lock className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900 text-sm mb-1">Encryption</h5>
                          <p className="text-xs text-slate-600">Data encrypted in transit and at rest</p>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200 flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Shield className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900 text-sm mb-1">Secure Authentication</h5>
                          <p className="text-xs text-slate-600">Industry-standard authentication systems</p>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200 flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900 text-sm mb-1">Regular Audits</h5>
                          <p className="text-xs text-slate-600">Continuous security monitoring and updates</p>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-green-200 flex items-start gap-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Database className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <h5 className="font-bold text-slate-900 text-sm mb-1">PCI DSS Compliant</h5>
                          <p className="text-xs text-slate-600">Secure payment processing via Stripe</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 6 - Your Rights */}
                <section id="rights" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <Eye className="w-6 h-6 text-indigo-700" />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">Your Privacy Rights</h2>
                  </div>
                  <div className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                    <p className="text-slate-700 leading-relaxed mb-6">
                      You have the following rights regarding your personal information:
                    </p>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h5 className="font-semibold text-slate-900 mb-1">Access</h5>
                          <p className="text-sm text-slate-600">Request a copy of the personal information we hold about you</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h5 className="font-semibold text-slate-900 mb-1">Correction</h5>
                          <p className="text-sm text-slate-600">Request correction of inaccurate or incomplete information</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h5 className="font-semibold text-slate-900 mb-1">Deletion</h5>
                          <p className="text-sm text-slate-600">Request deletion of your personal information (subject to legal obligations)</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-slate-200">
                        <CheckCircle2 className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h5 className="font-semibold text-slate-900 mb-1">Opt-Out</h5>
                          <p className="text-sm text-slate-600">Opt-out of marketing communications at any time</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Section 7 - Policy Updates */}
                <section id="updates" className="mb-12 scroll-mt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center shadow-sm">
                      <RefreshCw className="w-6 h-6 text-indigo-700" />
                    </div>
                    <h2 className="text-3xl font-bold text-slate-900">Policy Updates</h2>
                  </div>
                  <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                    <p className="text-slate-700 leading-relaxed text-lg mb-4">
                      We may update this Privacy Policy from time to time to reflect changes in our practices, technologies, legal requirements, or other factors.
                    </p>
                    <ul className="space-y-3">
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">We will notify you of material changes by posting the updated policy on our website</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">The &quot;Last Updated&quot; date at the top of this policy indicates when changes were made</span>
                      </li>
                      <li className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">Continued use of our platform after changes constitutes acceptance of the updated policy</span>
                      </li>
                    </ul>
                  </div>
                </section>

                {/* Contact Section */}
                <div className="mt-12 pt-8 border-t-2 border-slate-200">
                  <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-xl border border-indigo-200 shadow-lg">
                    <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center">
                        <Shield className="w-5 h-5 text-white" />
                      </div>
                      Privacy Questions or Concerns?
                    </h3>
                    <p className="text-slate-700 leading-relaxed mb-6">
                      If you have any questions about this Privacy Policy or our privacy practices, please contact us:
                    </p>
                    <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                      <p className="text-slate-900 font-bold text-lg mb-2">American Clinical Experience</p>
                      <p className="text-slate-900 font-semibold mb-4">Privacy Officer</p>
                      <div className="space-y-2 text-slate-700">
                        <p className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">📧</span>
                          <span>Privacy: <a href="mailto:privacy@americanclinicalexperience.com" className="text-indigo-600 hover:text-indigo-800 font-semibold">privacy@americanclinicalexperience.com</a></span>
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">💬</span>
                          <span>Support: <a href="mailto:support@americanclinicalexperience.com" className="text-indigo-600 hover:text-indigo-800 font-semibold">support@americanclinicalexperience.com</a></span>
                        </p>
                        <p className="flex items-center gap-2">
                          <span className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">🌐</span>
                          <span>Website: <a href="https://americanclinicalexperience.com" className="text-indigo-600 hover:text-indigo-800 font-semibold">americanclinicalexperience.com</a></span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-12 pt-8 border-t border-slate-200 text-center">
                  <p className="text-slate-500 text-sm mb-4">
                    © {new Date().getFullYear()} American Clinical Experience. All rights reserved.
                  </p>
                  <div className="flex justify-center gap-4">
                    <Link href="/terms" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                      Terms of Service
                    </Link>
                    <span className="text-slate-300">|</span>
                    <Link href="/" className="text-indigo-600 hover:text-indigo-800 text-sm font-medium">
                      Back to Home
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
